import React, { useMemo } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import i18n from 'i18n-react';

import DropZone from '../DropZone/DropZone';
import type { BookingFormField as BookingFormFieldType } from '../types';
import c from './_bookingFormField.module.scss';

export interface Props {
  field: BookingFormFieldType;
  value?: string;
  onChange: (_e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;

  arrivalDate?: string;
  departureDate?: string;

  fileUrl: string | null;
  uploading: boolean;
  onFilesSelected: (_files: File[]) => void;
}

export default function BookingFormField({
  field,
  value,
  onChange,
  arrivalDate,
  departureDate,
  fileUrl,
  uploading,
  onFilesSelected,
}: Props) {
  const req = field.required ? ' *' : '';

  const labelText = useMemo(() => {
    const label = i18n.translate(field.labelKey);
    return typeof label === 'string' ? label : '';
  }, [field.labelKey]);

  const placeholderText = useMemo(() => {
    const t = i18n.translate(field.placeholderKey ?? field.labelKey);
    return typeof t === 'string' ? t : '';
  }, [field.placeholderKey, field.labelKey]);

  const id = field.name;

  if (field.type === 'header') {
    return (
      <div className={clsx(c.item, field.size && c[field.size])}>
        <h4 className="booking-form-field__header">{labelText}</h4>
      </div>
    );
  }

  if (field.type === 'file') {
    return (
      <div className={clsx(c.item, field.size && c[field.size])}>
        <div className={c.label}>{labelText + req}</div>

        {!fileUrl && <DropZone onFilesSelected={onFilesSelected} disabled={uploading} />}

        {!!fileUrl && (
          <div className={c.fileIcon}>
            <FontAwesomeIcon icon={['fas', 'check-circle']} />
            <p>{i18n.translate('File has been uploaded successfully')}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={clsx(c.item, field.size && c[field.size])}>
      <label className={c.label} htmlFor={id}>
        {labelText + req}

        {field.type === 'select' && (
          <select
            id={id}
            className={c.control}
            name={field.name}
            value={value ?? ''}
            onChange={onChange}
            required={field.required}
          >
            <option value="" disabled>
              {i18n.translate('rewards.bookingForm.select')}
            </option>
            {field.options?.map((opt) => {
              return (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              );
            })}
          </select>
        )}

        {field.type === 'text' && (
          <input
            id={id}
            className={c.control}
            name={field.name}
            value={value ?? ''}
            placeholder={placeholderText || undefined}
            onChange={onChange}
            required={field.required}
          />
        )}

        {field.type === 'number' && (
          <input
            id={id}
            className={c.control}
            type="number"
            name={field.name}
            value={value ?? ''}
            placeholder={placeholderText || undefined}
            onChange={onChange}
            required={field.required}
            readOnly={field.name === 'totalNights' || field.name === 'lengthOfStay'}
            min={0}
          />
        )}

        {field.type === 'date' &&
          (() => {
            const isArrival = field.name === 'arrivalDate';
            const isDeparture = field.name === 'departureDate';

            return (
              <input
                id={id}
                className={c.control}
                type="date"
                name={field.name}
                value={value ?? ''}
                onChange={onChange}
                required={field.required}
                min={isDeparture ? arrivalDate : undefined}
                max={isArrival ? departureDate : undefined}
              />
            );
          })()}

        {field.type === 'currency' && (
          <div className={c.cost}>
            <div className={c.dollarSign}>{'$'}</div>
            <input
              id={id}
              className={c.control}
              type="number"
              name={field.name}
              value={value ?? ''}
              onChange={onChange}
              required={field.required}
              min={0}
              step="0.01"
              inputMode="decimal"
            />
          </div>
        )}
      </label>
    </div>
  );
}
